import { ThumbsDown, ThumbsUp } from 'lucide-react';
import { useCallback, useState } from 'react';

import {
    RUN_FEEDBACK_DOWNVOTE_PLACEHOLDER,
    type RunFeedbackLabel,
    RunFeedbackPrompt,
} from '@apps/web/components/shared/run_feedback/run_feedback_prompt';
import { Button } from '@apps/web/components/ui/button';
import { Spinner } from '@apps/web/components/ui/spinner';
import { useOrganizationId } from '@apps/web/hooks/use_organization_id';
import { getApiClient } from '@apps/web/lib/api_client';
import { toast } from '@apps/web/lib/app_notifications';
import { cn } from '@apps/web/lib/utils';
import {
    useUser,
    useVoiceCallRating,
} from '@apps/web/swr_data_fetching/data_fetching_hooks';
import {
    type RatingLabel,
    type VoiceCallRatingCreate,
    type VoiceCallRatingRead,
} from '@libraries/typescript/api_client';

type VoiceCallRatingProps = {
    readonly voiceCallId: string;
    readonly initialRating?: VoiceCallRatingRead | null;
    readonly compact?: boolean;
};

export function VoiceCallRating({
    voiceCallId,
    initialRating,
    compact = false,
}: VoiceCallRatingProps) {
    const organizationId = useOrganizationId();
    const { data: user } = useUser({ reason: 'voice-call-rating' });
    const isAdmin = user?.admin ?? false;

    const {
        data: ratingResponse,
        mutate,
        isLoading,
    } = useVoiceCallRating(organizationId, voiceCallId, isAdmin, {
        reason: 'voice-call-rating',
        initialData:
            initialRating === undefined ? undefined : { rating: initialRating },
    });

    const rating = ratingResponse?.rating ?? null;
    const selectedLabel: RatingLabel | null = rating?.label ?? null;

    const [isSaving, setIsSaving] = useState(false);
    // Set while a thumbs-down note is being written. Nothing is persisted until
    // Save, so the reason travels with the rating.
    const [pendingLabel, setPendingLabel] = useState<RunFeedbackLabel | null>(
        null,
    );
    const [noteDraft, setNoteDraft] = useState('');

    const ratingPath = useCallback(() => {
        if (isAdmin) {
            return {
                url: '/admin/voice-calls/{voice_call_id}/rating' as const,
                pathParams: { voice_call_id: voiceCallId },
            };
        }
        return {
            url: '/organizations/{organization_id}/voice-calls/{voice_call_id}/rating' as const,
            pathParams: {
                organization_id: organizationId ?? '',
                voice_call_id: voiceCallId,
            },
        };
    }, [isAdmin, organizationId, voiceCallId]);

    const saveRating = useCallback(
        async (label: RatingLabel, comment: string | null) => {
            const optimistic = {
                rating: {
                    id: rating?.id ?? '',
                    call_id: rating?.call_id ?? voiceCallId,
                    created_by_user_id: rating?.created_by_user_id ?? null,
                    source: rating?.source ?? ('internal' as const),
                    label,
                    comment,
                    created_at: rating?.created_at ?? new Date().toISOString(),
                    updated_at: new Date().toISOString(),
                },
            };
            setIsSaving(true);
            try {
                await mutate(
                    async () => {
                        const { url, pathParams } = ratingPath();
                        await getApiClient().put({
                            url,
                            pathParams,
                            body: {
                                label,
                                comment,
                            } satisfies VoiceCallRatingCreate,
                        });
                        return optimistic;
                    },
                    {
                        optimisticData: optimistic,
                        rollbackOnError: true,
                        revalidate: false,
                        populateCache: true,
                    },
                );
                return true;
            } catch {
                toast.error('Failed to save rating', {
                    persistNotification: true,
                    notificationType: 'outreach_action',
                });
                await mutate();
                return false;
            } finally {
                setIsSaving(false);
            }
        },
        [mutate, rating, ratingPath, voiceCallId],
    );

    const clearRating = useCallback(async () => {
        setIsSaving(true);
        try {
            await mutate(
                async () => {
                    const { url, pathParams } = ratingPath();
                    await getApiClient().delete({ url, pathParams });
                    return { rating: null };
                },
                {
                    optimisticData: { rating: null },
                    rollbackOnError: true,
                    revalidate: false,
                    populateCache: true,
                },
            );
        } catch {
            toast.error('Failed to clear rating', {
                persistNotification: true,
                notificationType: 'outreach_action',
            });
            await mutate();
        } finally {
            setIsSaving(false);
        }
    }, [mutate, ratingPath]);

    const handleThumbUp = () => {
        if (selectedLabel === 'good') {
            void clearRating();
            return;
        }
        setPendingLabel(null);
        void saveRating('good', null);
    };

    const handleThumbDown = () => {
        // Editing an existing downvote pre-fills its saved reason; the thumbs
        // only render while pendingLabel is null, so no need to re-check it.
        setNoteDraft(selectedLabel === 'bad' ? (rating?.comment ?? '') : '');
        setPendingLabel('bad');
    };

    const closeNote = () => {
        setPendingLabel(null);
        setNoteDraft('');
    };

    const handleSaveNote = async () => {
        const trimmed = noteDraft.trim();
        if (!trimmed) {
            return;
        }
        // Keep the note open (and the draft intact) if the save failed, so the
        // reviewer does not silently lose their in-progress feedback.
        const saved = await saveRating('bad', trimmed || null);
        if (saved) {
            closeNote();
        }
    };

    if (isLoading) {
        return <div className="text-muted-foreground text-sm">Loading…</div>;
    }

    if (pendingLabel !== null) {
        return (
            <div
                onClick={
                    compact ? (event) => event.stopPropagation() : undefined
                }
                onKeyDown={
                    compact ? (event) => event.stopPropagation() : undefined
                }
            >
                <RunFeedbackPrompt
                    state="note"
                    comment={noteDraft}
                    disabled={isSaving}
                    requireComment={true}
                    autoFocusComment={true}
                    submitOnEnter={true}
                    commentPlaceholder={RUN_FEEDBACK_DOWNVOTE_PLACEHOLDER}
                    onCommentChange={setNoteDraft}
                    onSaveComment={() => void handleSaveNote()}
                    onSkipComment={closeNote}
                    onShowNote={() => undefined}
                    className={compact ? 'min-w-64' : undefined}
                />
            </div>
        );
    }

    return (
        <div
            className="flex items-center gap-2"
            onClick={compact ? (event) => event.stopPropagation() : undefined}
            onKeyDown={compact ? (event) => event.stopPropagation() : undefined}
        >
            {!compact ? (
                <span className="text-muted-foreground text-sm font-medium">
                    Rate call
                </span>
            ) : null}
            <Button
                variant="outline"
                size="icon"
                className={cn(
                    compact ? 'h-7 w-7' : 'h-8 w-8',
                    selectedLabel === 'good' &&
                        'bg-green-100 text-green-600 hover:bg-green-200 hover:text-green-700',
                )}
                onClick={handleThumbUp}
                disabled={isSaving}
                aria-label="Looks good"
                title="Looks good"
            >
                <ThumbsUp
                    className={cn(
                        'h-4 w-4',
                        selectedLabel === 'good' && 'fill-current',
                    )}
                />
            </Button>
            <Button
                variant="outline"
                size="icon"
                className={cn(
                    compact ? 'h-7 w-7' : 'h-8 w-8',
                    selectedLabel === 'bad' &&
                        'bg-red-100 text-red-600 hover:bg-red-200 hover:text-red-700',
                )}
                onClick={handleThumbDown}
                disabled={isSaving}
                aria-label="Needs work"
                title="Needs work"
            >
                <ThumbsDown
                    className={cn(
                        'h-4 w-4',
                        selectedLabel === 'bad' && 'fill-current',
                    )}
                />
            </Button>
            {isSaving && <Spinner className="text-muted-foreground h-4 w-4" />}
        </div>
    );
}
